<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { toast } from 'dashboard/helper/toast';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SectionLayout from './SectionLayout.vue';

const { t } = useI18n();
const store = useStore();

const apiKeyStatus = computed(() => store.getters['ivrAudios/getApiKeyStatus']);
const uiFlags = computed(() => store.getters['ivrAudios/getUIFlags']);

const apiKeyInput = ref('');
const showApiKeyInput = ref(false);
const showApiKeyPassword = ref(false);
const apiKeyTestResult = ref(null);

const toggleApiKeyEdit = () => {
  showApiKeyInput.value = !showApiKeyInput.value;
  if (!showApiKeyInput.value) {
    apiKeyInput.value = '';
    apiKeyTestResult.value = null;
    showApiKeyPassword.value = false;
  }
};

const testApiKey = async () => {
  if (!apiKeyInput.value.trim()) {
    toast.error(t('TELEPHONY.IVR.API_KEY.ENTER_KEY'));
    return;
  }

  try {
    const result = await store.dispatch(
      'ivrAudios/testApiKey',
      apiKeyInput.value.trim()
    );
    apiKeyTestResult.value = result;
    toast.success(t('TELEPHONY.IVR.API_KEY.VALID'));
  } catch (error) {
    apiKeyTestResult.value = {
      valid: false,
      error:
        error.response?.data?.error || t('TELEPHONY.IVR.API_KEY.TEST_ERROR'),
    };
    toast.error(apiKeyTestResult.value.error);
  }
};

const saveApiKey = async () => {
  if (!apiKeyInput.value.trim()) {
    toast.error(t('TELEPHONY.IVR.API_KEY.ENTER_KEY'));
    return;
  }

  try {
    const result = await store.dispatch(
      'ivrAudios/saveApiKey',
      apiKeyInput.value.trim()
    );
    if (result.success) {
      toast.success(result.message);
      showApiKeyInput.value = false;
      apiKeyInput.value = '';
      apiKeyTestResult.value = null;
      showApiKeyPassword.value = false;
      await store.dispatch('ivrAudios/fetchApiKeyStatus');
    }
  } catch (error) {
    toast.error(
      error.response?.data?.error || t('TELEPHONY.IVR.API_KEY.SAVE_ERROR')
    );
  }
};

const removeApiKey = async () => {
  try {
    const result = await store.dispatch('ivrAudios/saveApiKey', '');
    if (result.success) {
      toast.success(result.message);
      showApiKeyInput.value = false;
      await store.dispatch('ivrAudios/fetchApiKeyStatus');
    }
  } catch (error) {
    toast.error(
      error.response?.data?.error || t('TELEPHONY.IVR.API_KEY.REMOVE_ERROR')
    );
  }
};

onMounted(() => {
  store.dispatch('ivrAudios/fetchApiKeyStatus');
});
</script>

<template>
  <SectionLayout
    :title="t('TELEPHONY.IVR.API_KEY.TITLE')"
    :description="t('TELEPHONY.IVR.API_KEY.SETTINGS_DESCRIPTION')"
  >
    <template #headerActions>
      <span
        v-if="apiKeyStatus.configured"
        class="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-n-teal-3 text-n-teal-11"
      >
        <span class="i-lucide-check-circle size-3.5" />
        {{ t('TELEPHONY.IVR.API_KEY.CONFIGURED') }}
      </span>
      <span
        v-else
        class="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-n-amber-3 text-n-amber-11"
      >
        <span class="i-lucide-alert-circle size-3.5" />
        {{ t('TELEPHONY.IVR.API_KEY.NOT_CONFIGURED') }}
      </span>
    </template>

    <!-- Configured + not editing -->
    <div
      v-if="!showApiKeyInput && apiKeyStatus.configured"
      class="flex items-center justify-between"
    >
      <div class="text-sm text-n-slate-11">
        <span class="font-mono bg-n-alpha-2 px-2 py-1 rounded">
          {{ apiKeyStatus.maskedKey }}
        </span>
      </div>
      <div class="flex gap-2">
        <Button
          icon="i-lucide-pencil"
          color="slate"
          variant="ghost"
          size="xs"
          @click="toggleApiKeyEdit"
        >
          {{ t('TELEPHONY.IVR.API_KEY.CHANGE') }}
        </Button>
        <Button
          icon="i-lucide-trash-2"
          color="ruby"
          variant="ghost"
          size="xs"
          @click="removeApiKey"
        >
          {{ t('TELEPHONY.IVR.API_KEY.REMOVE') }}
        </Button>
      </div>
    </div>

    <!-- Not configured or editing -->
    <div v-if="!apiKeyStatus.configured || showApiKeyInput">
      <p v-if="!apiKeyStatus.configured" class="text-sm text-n-slate-11 mb-3">
        {{ t('TELEPHONY.IVR.API_KEY.DESCRIPTION') }}
        <a
          href="https://elevenlabs.io/app/settings/api-keys"
          target="_blank"
          rel="noopener noreferrer"
          class="text-n-brand-ink hover:underline"
        >
          {{ t('TELEPHONY.IVR.API_KEY.GET_KEY_LINK') }}
        </a>
      </p>

      <div class="flex gap-2">
        <div class="flex-1 relative">
          <Input
            v-model="apiKeyInput"
            :type="showApiKeyPassword ? 'text' : 'password'"
            placeholder="sk_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            class="pr-10"
          />
          <button
            type="button"
            class="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-n-alpha-2 transition-colors"
            @click="showApiKeyPassword = !showApiKeyPassword"
          >
            <span
              class="size-4 text-n-slate-11"
              :class="[
                showApiKeyPassword ? 'i-lucide-eye-off' : 'i-lucide-eye',
              ]"
            />
          </button>
        </div>
        <Button
          icon="i-lucide-flask-conical"
          color="slate"
          variant="faded"
          size="sm"
          :is-loading="uiFlags.isTestingApiKey"
          :disabled="!apiKeyInput.trim()"
          @click="testApiKey"
        >
          {{ t('TELEPHONY.IVR.API_KEY.TEST') }}
        </Button>
        <Button
          icon="i-lucide-save"
          color="blue"
          size="sm"
          :is-loading="uiFlags.isSavingApiKey"
          :disabled="!apiKeyInput.trim()"
          @click="saveApiKey"
        >
          {{ t('TELEPHONY.COMMON.SAVE') }}
        </Button>
        <Button
          v-if="showApiKeyInput"
          icon="i-lucide-x"
          color="slate"
          variant="ghost"
          size="sm"
          @click="toggleApiKeyEdit"
        >
          {{ t('TELEPHONY.COMMON.CANCEL') }}
        </Button>
      </div>

      <!-- Test result -->
      <div
        v-if="apiKeyTestResult"
        class="mt-3 p-3 rounded-lg text-sm"
        :class="[
          apiKeyTestResult.valid
            ? 'bg-n-teal-3 text-n-teal-11'
            : 'bg-n-ruby-3 text-n-ruby-11',
        ]"
      >
        <div v-if="apiKeyTestResult.valid" class="flex items-center gap-2">
          <span class="i-lucide-check-circle size-4" />
          <span v-if="apiKeyTestResult.subscription">
            {{
              t('TELEPHONY.IVR.API_KEY.TEST_RESULT_VALID', {
                tier: apiKeyTestResult.subscription?.tier,
                used: apiKeyTestResult.subscription?.character_count?.toLocaleString(),
                limit:
                  apiKeyTestResult.subscription?.character_limit?.toLocaleString(),
              })
            }}
          </span>
          <span v-else>
            {{ t('TELEPHONY.IVR.API_KEY.TEST_RESULT_VALID_NO_QUOTA') }}
          </span>
        </div>
        <div v-else class="flex items-center gap-2">
          <span class="i-lucide-x-circle size-4" />
          <span>{{ apiKeyTestResult.error }}</span>
        </div>
      </div>
    </div>
  </SectionLayout>
</template>
